import Link from 'next/link';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { trackAdobeClick } from '@/utils/adobe/click';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import { resultItemVariants } from './ResultItem.styles';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SearchHit {
  id: string;
  url: string;
  title: string;
  highlight: string;
  meta_description: string;
  categories: string[];
  images?: { main?: string; capture?: string };
  custom_fields?: Record<string, string | string[]>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
type ResultType = 'srpnet' | 'blog' | 'media';

/** Extract the site label from a URL's hostname (e.g. "https://srpnet.com/…" → "SRPNET"). */
const getSiteLabel = (url: string): ResultType => {
  try {
    const hostname = new URL(url).hostname;
    const parts = hostname.replace(/^www\./, '').split('.');
    return parts[0] as ResultType;
  } catch {
    return 'srpnet';
  }
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface ResultItemProps {
  hit: SearchHit;
  query: string;
  isResultsPageItem?: boolean;
  onClick?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ResultItem = ({ hit, isResultsPageItem = false, onClick }: ResultItemProps) => {
  const siteLabel = getSiteLabel(hit.url);
  const imageUrl = hit.images?.main;
  const isPdf = hit.categories?.some((c) => c.includes('doctype_pdf')) || hit.url.endsWith('.pdf');

  const {
    resultItem,
    itemWrapper,
    imageWrapper,
    resultImage,
    pdfIcon,
    resultContent,
    resultTitle,
    resultSnippet,
    resultBadge,
  } = resultItemVariants({ resultType: siteLabel, isResultPageItem: isResultsPageItem });

  // On the results page a result click is plain navigation — track it. The header dropdown
  // passes its own onClick (search-selection tracking), so only fire here when none was provided.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isResultsPageItem) {
      trackAdobeClick(
        { clickName: hit.title, clickLocation: 'Search-Results', clickType: 'search-result-link' },
        e
      );
    }
    onClick?.();
  };

  return (
    <Link href={hit.url} onClick={handleClick} className={resultItem()}>
      {isResultsPageItem && <RichTextWrapper className={resultTitle()} content={hit.title} />}
      <div className={itemWrapper()}>
        {isResultsPageItem && (
          <span className={imageWrapper()}>
            {isPdf ? (
              <i className={pdfIcon()} aria-hidden="true" />
            ) : (
              // Search hits carry only a thumbnail URL, so the wrapper gets a synthesized asset
              // sized to the 98px thumbnail box; it renders unoptimized, so external hosts are fine
              imageUrl && (
                <ImageWrapper
                  image={toEnhancedImage(
                    { url: imageUrl },
                    { fallbackWidth: 98, fallbackHeight: 98 }
                  )}
                  alt=""
                  wrapperClassName="w-full h-full"
                  imageClassName={resultImage()}
                />
              )
            )}
          </span>
        )}
        <div className={resultContent()}>
          {!isResultsPageItem && <RichTextWrapper className={resultTitle()} content={hit.title} />}
          <RichTextWrapper className={resultSnippet()} content={hit.highlight || ''} />
          {siteLabel && <span className={resultBadge()}>{siteLabel}</span>}
        </div>
      </div>
    </Link>
  );
};
